import { gql, useMutation, useQuery } from '@apollo/client';
import { validate, ValidationError } from 'class-validator';
import { FC, FormEvent, MouseEvent, useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTwitterSquare } from '@fortawesome/free-brands-svg-icons/faTwitterSquare';
import { faFacebookSquare } from '@fortawesome/free-brands-svg-icons/faFacebookSquare';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons/faLinkedin';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import cx from 'classnames';
import { useAmplitude } from '../amplitude/amplitudeContext';
import Agent from '../models/Agent';
import styles from './AgentProfile.module.scss';
import AgentContactForm, { ContactMethod } from '../models/AgentContactForm';
import Button from '../components/Button';
import FormInput from '../components/FormInput';
import Radio from '../components/Radio';
import useWindowSize from '../hooks/useWindowSize';
import CheckMarkInCircle from '../components/CheckMarkInCircle';
import { useSerializedState } from '../hooks/useSerialized';
import { Skeleton } from '../components/Skeleton';
import Image from '../components/Image';

export const getAgentById = gql`
  query GetAgentById($id: ID!) {
    agent(id: $id) {
      activeLocations {
        city
        state
      }
      bio
      asab
      cityState
      email
      firstName
      lastName
      phoneNumber
      photo
      preferredName
      website
      twitter
      facebook
      linkedIn
      state
      stateBroker
      title
    }
  }
`;

const ContactAgent = gql`
  mutation ContactAgent($input: ContactAgent!) {
    contactAgent(input: $input)
  }
`;

const AgentProfile: FC = () => {
  const { t } = useTranslation();
  const match = useRouteMatch<{ nameId: string }>();
  const history = useHistory();
  const { nameId } = match.params;
  const parts = nameId.split('-');
  const id = parts[parts.length - 1];

  const [contactForm, setContactForm] = useState(new AgentContactForm());
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [, width] = useWindowSize();

  const { loading, data } = useQuery<{
    agent: Agent;
  }>(getAgentById, { variables: { id } });
  const [contactAgentMutation, { data: contactData, loading: contactLoading, error: contactError }] = useMutation<{ contactAgent: string }, { input: AgentContactForm }>(ContactAgent);
  const [agent, setAgent] = useSerializedState(Agent, data?.agent);
  const rawAgent = data?.agent;
  useEffect(() => {
    setAgent(rawAgent);
  }, [rawAgent, setAgent]);

  useEffect(() => {
    setContactForm(
      (form) =>
        new AgentContactForm({
          ...form,
          agentId: id,
        })
    );
  }, [id]);

  const { logAmplitudeEvent } = useAmplitude();

  useEffect(() => {
    const defaultTitle = document.title;
    if (agent?.fullName) {
      document.title = `${agent?.fullName} - ${agent?.cityState} Real Estate Agent | eXp Realty®`;
    }
    return () => {
      document.title = defaultTitle;
    };
  }, [agent]);

  async function submitForm(e: FormEvent<HTMLFormElement>) {
    logAmplitudeEvent('click on contact form submit button');
    e.preventDefault();
    const errors = await validate(contactForm);
    if (errors.length) {
      setValidationErrors(errors);
      return;
    }

    await contactAgentMutation({ variables: { input: contactForm } }).catch(() => null);
  }

  const back = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      history.goBack();
    },
    [history]
  );

  const formRef = useRef<HTMLFormElement>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const scrollToForm = useCallback(() => {
    nameInputRef.current?.focus();

    if (!formRef.current) {
      return;
    }

    window.scrollTo({
      top: formRef.current.offsetTop,
      behavior: 'smooth',
    });
  }, [nameInputRef, formRef]);

  const skeleton = loading || !agent;

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.content}>
          <p>eXp {t('Agent Directory')}</p>
          <p>/</p>
          <Link
            to="/"
            onClick={(e) => {
              back(e);
              logAmplitudeEvent('click on return to search');
            }}
            data-test="back"
          >
            {t('Return to Search')}
          </Link>
        </div>
      </div>

      <div className={styles.main}>
        <h1 className={styles.name} data-test="name">
          {skeleton ? <Skeleton width="40rem" /> : agent?.fullName}
        </h1>
        {/* TODO use provided title once api is providing */}
        <h2 className={styles.title} data-test="title">
          {skeleton ? <Skeleton width="25rem" /> : agent?.title}
        </h2>
        <div className={styles.layout}>
          <div data-test="info-container" className={styles.info}>
            <Image alt={`${agent?.fullName} - ${agent?.cityState} Real Estate Agent`} className={styles.img} fallBack={Skeleton} src={agent?.photo} />
            {skeleton ? (
              <Skeleton className={styles.button} count={2} />
            ) : (
              <>
                <Button
                  data-test="contact-button"
                  className={styles.button}
                  active
                  onClick={() => {
                    scrollToForm();
                    logAmplitudeEvent('click on contact agent button');
                  }}
                >
                  {`${t('Contact')} ${agent?.name}`}
                </Button>
                <Button
                  data-test="join-button"
                  onClick={(e) =>
                    logAmplitudeEvent('click on eXp realty join button', {
                      agentFullName: agent?.fullName,
                      agentEmail: agent?.email,
                    })
                  }
                  className={styles.button}
                  href="https://exprealty.com/join"
                >
                  {t('Join eXp Realty')}
                </Button>
              </>
            )}
            <hr />
            <strong>{t('Contact Info')}</strong>
            {skeleton && <Skeleton count={3} />}
            {agent?.phoneNumber && (
              <a className={styles.sub} href={`tel:${agent.phoneNumber}`} onClick={(e) => logAmplitudeEvent('click on call agent button')} data-test="info-phone">
                {agent.phoneNumber}
              </a>
            )}
            {agent?.email && (
              <a className={styles.sub} href={`mailto:${agent.email}`} onClick={(e) => logAmplitudeEvent('click on email agent button')} data-test="info-email">
                {agent?.email}
              </a>
            )}
            {agent?.website && (
              <a
                className={styles.url}
                onClick={(e) => logAmplitudeEvent('click on agent website link')}
                target=" _blank"
                rel="noopener noreferrer"
                href={agent.website.startsWith('http') ? agent.website : `http://${agent.website}`}
                data-test="info-website"
              >
                {agent.website.replace(/^https?:\/\//, '')}
              </a>
            )}
            {agent?.facebookUrl ? (
              <a target="_blank" onClick={(e) => logAmplitudeEvent('click on Facebook link')} data-test="agent-facebook" rel="noopener noreferrer" href={agent.facebookUrl}>
                <FontAwesomeIcon className={styles.facebookIcon} icon={faFacebookSquare} />
              </a>
            ) : null}
            {agent?.linkedInUrl ? (
              <a target="_blank" onClick={(e) => logAmplitudeEvent('click on LinkedIn link')} data-test="agent-linkedin" rel="noopener noreferrer" href={agent.linkedInUrl}>
                <FontAwesomeIcon className={styles.linkedinIcon} icon={faLinkedin} />
              </a>
            ) : null}
            {agent?.twitterUrl ? (
              <a target="_blank" onClick={(e) => logAmplitudeEvent('click on Twitter link')} data-test="agent-twitter" rel="noopener noreferrer" href={agent.twitterUrl}>
                <FontAwesomeIcon className={styles.twitterIcon} icon={faTwitterSquare} />
              </a>
            ) : null}
            <strong>{t('Areas Served')}</strong>
            {skeleton && <Skeleton count={4} />}
            {agent?.activeLocations
              ?.reduce((acc, cur) => {
                const loc = `${cur.city}, ${cur.state}`;

                if (!~acc.findIndex((l) => l === loc)) {
                  acc.push(loc);
                }

                return acc;
              }, [] as string[])
              .map((l) => (
                <div className={styles.sub} key={l} data-test="area-served">
                  {l}
                </div>
              ))}
            <hr />
          </div>
          <div className={styles.bioContainer}>
            {(agent?.bio || skeleton) && <div className={styles.bio}>{skeleton ? <Skeleton count={6} /> : agent?.bio?.replaceAll(/<[^<]+?>/g, '')}</div>}
            {contactData?.contactAgent && (
              <div data-test="form-success-message" className={styles.form}>
                <h2>{t('Thank you!')}</h2>
                <div className={styles.successContainer}>
                  <div className={styles.lineContainer}>
                    <div className={styles.line} />
                    <CheckMarkInCircle />
                  </div>
                  <div className={styles.successHeader}>{`Thanks for sending ${agent?.name} your message!`}</div>
                </div>

                <div className={styles.successMessage}>
                  They will get back to you shortly to help with your real estate needs. Whether you are looking to buy, sell or rent, our experienced and passionate agents are on hand to help make it
                  happen!
                </div>
                <div className={styles.successMessage}>
                  While you’re waiting, check out some homes for sale in your area at{' '}
                  <a className={styles.url} target=" _blank" rel="noopener noreferrer" href="www.exprealty.com" data-test="thank-you-link">
                    www.exprealty.com
                  </a>
                </div>
              </div>
            )}
            {contactError && (
              <div className={styles.form}>
                <h3>Something went wrong. Please try again later.</h3>
              </div>
            )}
            {!(contactData?.contactAgent || contactError) && (
              <form onSubmit={submitForm} className={cx(styles.form, { [styles.hidden]: skeleton })} ref={formRef}>
                <h2 data-test="contact-title" className={styles.contactTitle}>
                  <span>{t('Contact')}</span> {agent?.fullName}
                </h2>
                <hr />
                <div className={styles.fifty}>
                  <FormInput
                    label={t('First Name')}
                    id="firstName"
                    ref={nameInputRef}
                    error={validationErrors.find((err) => err.property === 'firstName')}
                    value={contactForm.firstName}
                    onChange={(value) =>
                      setContactForm(
                        new AgentContactForm({
                          ...contactForm,
                          firstName: value,
                        })
                      )
                    }
                    required
                    data-test="form-firstName"
                  />
                  <FormInput
                    label={t('Last Name')}
                    id="lastName"
                    error={validationErrors.find((err) => err.property === 'lastName')}
                    value={contactForm.lastName}
                    onChange={(value) =>
                      setContactForm(
                        new AgentContactForm({
                          ...contactForm,
                          lastName: value,
                        })
                      )
                    }
                    required
                    data-test="form-lastName"
                  />
                </div>
                {width > 700 ? (
                  <Radio
                    label={t('Contact Preference')}
                    name="contactPreference"
                    options={[
                      { label: t('Email'), id: ContactMethod.EMAIL },
                      { label: t('Text/SMS'), id: ContactMethod.SMS },
                      { label: t('Phone'), id: ContactMethod.PHONE },
                      { label: t('No Preference'), id: 'null' },
                    ]}
                    value={contactForm.preferredMethod || 'null'}
                    onChange={(opt) =>
                      setContactForm(
                        new AgentContactForm({
                          ...contactForm,
                          preferredMethod: opt.id === 'null' ? null : ContactMethod[opt.id as keyof typeof ContactMethod],
                        })
                      )
                    }
                    data-test="form-contactPreference-radio"
                  />
                ) : (
                  <div>
                    <div className={styles.label}>{t('Contact Preference')}</div>
                    <FormInput
                      value={contactForm.preferredMethod || 'null'}
                      options={[
                        { label: t('Email'), value: ContactMethod.EMAIL },
                        { label: t('Text/SMS'), value: ContactMethod.SMS },
                        { label: t('Phone'), value: ContactMethod.PHONE },
                        { label: t('No Preference'), value: 'null' },
                      ]}
                      onChange={(opt) => {
                        setContactForm(
                          new AgentContactForm({
                            ...contactForm,
                            preferredMethod: ContactMethod[opt as keyof typeof ContactMethod],
                          })
                        );
                      }}
                      data-test="form-preferredMethod-dropdown"
                      dropdown
                    />
                  </div>
                )}
                <div className={styles.fifty}>
                  <FormInput
                    label={t('Email')}
                    error={validationErrors.find((err) => err.property === 'email')}
                    id="email"
                    value={contactForm.email}
                    onChange={(value) =>
                      setContactForm(
                        new AgentContactForm({
                          ...contactForm,
                          email: value || undefined,
                        })
                      )
                    }
                    data-test="form-email"
                  />
                  <FormInput
                    label={t('Mobile Phone')}
                    error={validationErrors.find((err) => err.property === 'mobilePhone')}
                    id="phone"
                    value={contactForm.mobilePhone}
                    onChange={(value) =>
                      setContactForm(
                        new AgentContactForm({
                          ...contactForm,
                          mobilePhone: value || undefined,
                        })
                      )
                    }
                    data-test="form-phone"
                  />
                </div>
                <FormInput
                  label={t('Message')}
                  error={validationErrors.find((err) => err.property === 'message')}
                  id="message"
                  value={contactForm.message}
                  onChange={(value) =>
                    setContactForm(
                      new AgentContactForm({
                        ...contactForm,
                        message: value,
                      })
                    )
                  }
                  multiline
                  data-test="form-message"
                />
                <Button type="submit" disabled={contactLoading} active data-test="form-submit">
                  {contactLoading ? t('Submitting') : t('Submit')}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentProfile;
